import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface AdminRouteProps {
  children: React.ReactNode;
}

type VerifyState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'forbidden' }
  | { status: 'authorized' };

/**
 * Hardened admin route guard.
 *
 * - Re-validates the session server-side with `supabase.auth.getUser()`
 *   (does NOT trust cached client state).
 * - Re-checks the `admin` role server-side via the security-definer
 *   `has_role` RPC on every mount / route change.
 * - Re-verifies when the auth state changes (login/logout in another tab).
 * - Renders nothing sensitive until verification completes.
 */
const AdminRoute = ({ children }: AdminRouteProps) => {
  const location = useLocation();
  const [state, setState] = useState<VerifyState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      setState({ status: 'loading' });

      // 1) Re-validate the user against the auth server (not the local cache).
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (cancelled) return;

      if (userError || !userData?.user) {
        setState({ status: 'unauthenticated' });
        return;
      }

      // 2) Re-check role server-side. Never trust client-side claims.
      const { data: hasAdmin, error: roleError } = await supabase.rpc('has_role', {
        _user_id: userData.user.id,
        _role: 'admin',
      });
      if (cancelled) return;

      if (roleError || hasAdmin !== true) {
        setState({ status: 'forbidden' });
        return;
      }

      setState({ status: 'authorized' });

      // 3) Audit log: record this admin page access (best-effort, non-blocking).
      supabase
        .rpc('log_admin_event', {
          p_action_type: 'admin_page_access',
          p_entity_type: 'admin_panel',
          p_entity_id: null,
          p_metadata: {
            path: location.pathname,
            search: location.search || null,
            referrer: typeof document !== 'undefined' ? document.referrer || null : null,
          },
          p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        })
        .then(({ error }) => {
          if (error) console.debug('Audit log skipped:', error.message);
        });
    };

    verify();

    // Re-verify on auth state changes (sign-out / token refresh / other tab).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      verify();
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [location.pathname]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Vérification des permissions...</p>
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  if (state.status === 'forbidden') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-4xl font-bold mb-4">Accès refusé</h1>
          <p className="text-muted-foreground">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminRoute;
